import PropTypes from "prop-types";
import { createContext, useEffect, useReducer } from "react";

import extendedFieldActions from "./actions";
import extendedFieldHooks from "./selectorHooks";

const ExtendedFieldsContext = createContext();

const shareWithOptions = {
  user: "user",
  household: "household",
  spouse: "spouse",
};

// Note that these keys are reused to allow state to be passed to create/enable endpoints.
const defaultFieldData = {
  networkFieldID: undefined,
  fieldTypeID: 1, // only modified by INIT_SELECTED_FIELD
  visibility: "default", // only modified by INIT_SELECTED_FIELD
  isCustomField: false, // only modified by INIT_SELECTED_FIELD
  icon: "fa-church fas",
  title: "",
  fieldResponseViewPermissionID: 1,
  fieldResponseEditPermissionID: 1,
  showOnSignUp: 1,
  iigOn: true,
  isUsedBySmartGroup: false,
  parentShared: false,
  notDeletable: false,
  periodicIIGOn: false,
  fieldTypeInstanceID: undefined,
  configuration: {
    behaviorOptions: {
      // add for selectlist type
      allowMultiple: false, // true of false
      // add this property and value for multiline text:
      // displayAs: "multi",
    },
    responseOptions: {
      // add for selectlist type
      responseOptionList: [],
      // add for number type
      min: undefined,
      max: undefined,
      // add for address type
      default: { country: "US" },
    },
    // add for address type
    subfields: {
      addr1: { isVisible: 1 },
      city: { isVisible: 1 },
      state: { isVisible: 1 },
      zip: { isVisible: 1 },
      country: { isVisible: 0 }, // can be 0 for custom fields
      year: { isVisible: 1 }, // can be 0 for custom fields
    },
  },
  orderNumber: undefined,
  groupID: undefined,
  createdByID: undefined,
  uniqueKey: undefined,
  fieldResponseEntity: "user",
  blockSharing: 1, // default to things not displaying whenever possible.
  required: false,
  copyFromOtherSignup: false,
};

const extendedFieldsReducer = (state, action) => {
  const {
    type,
    groupId,
    signupItemId,
    extendedFields,
    networkFieldID,
    fieldData, // This will contain many of the other properties being destructured, and is used to overwrite them at scale.
    icon,
    title,
    fieldResponseViewPermissionID,
    fieldResponseEditPermissionID,
    showOnSignUp,
    iigOn,
    periodicIIGOn,
    fieldResponseEntity,
    multiline,
    min,
    max,
    changeCountry,
    defaultCountry,
    includeYear,
    options,
    multiOptions,
    isNavigating,
    errorMessage,
    displayIsFullWidth,
    isEveryoneGroup,
    required,
    editWithModal,
    isRefreshing,
  } = action;
  switch (type) {
    case extendedFieldActions.INIT_SELECTED_FIELD:
      return { ...state, ...defaultFieldData, ...fieldData };
    case extendedFieldActions.SET_GROUP_ID:
      return { ...state, groupId };
    case extendedFieldActions.SET_SIGNUP_ITEM_ID:
      return { ...state, signupItemId };
    case extendedFieldActions.SET_EXTENDED_FIELDS:
      return { ...state, extendedFields, isSavingSignupField: false };
    case extendedFieldActions.SET_SELECTED_FIELD_ID:
      return { ...state, networkFieldID };
    case extendedFieldActions.SET_ICON:
      return { ...state, icon };
    case extendedFieldActions.SET_TITLE:
      return { ...state, title };
    case extendedFieldActions.SET_REQUIRED:
      return { ...state, required };
    case extendedFieldActions.SET_EDIT_PERMISSION_ID:
      return { ...state, fieldResponseEditPermissionID };
    case extendedFieldActions.SET_VIEW_PERMISSION_ID:
      return { ...state, fieldResponseViewPermissionID };
    case extendedFieldActions.SET_SHOW_ON_SIGNUP:
      return { ...state, showOnSignUp };
    case extendedFieldActions.SET_IIG:
      return { ...state, iigOn };
    case extendedFieldActions.SET_PERIODIC_IIG:
      return { ...state, periodicIIGOn };
    case extendedFieldActions.SET_SHARE_WITH:
      return { ...state, fieldResponseEntity };
    case extendedFieldActions.SET_MULTILINE:
      return {
        ...state,
        configuration: {
          ...state.configuration,
          behaviorOptions: { displayAs: multiline ? "multi" : undefined },
        },
      };
    case extendedFieldActions.SET_MIN:
      return {
        ...state,
        configuration: {
          ...state.configuration,
          responseOptions: { ...state.configuration.responseOptions, min },
        },
      };
    case extendedFieldActions.SET_MAX:
      return {
        ...state,
        configuration: {
          ...state.configuration,
          responseOptions: { ...state.configuration.responseOptions, max },
        },
      };
    case extendedFieldActions.SET_CHANGE_COUNTRY:
      return {
        ...state,
        configuration: {
          ...state.configuration,
          subfields: {
            ...state.configuration.subfields,
            country: { isVisible: Number(changeCountry) },
          },
        },
      };
    case extendedFieldActions.SET_DEFAULT_COUNTRY:
      return {
        ...state,
        configuration: {
          ...state.configuration,
          responseOptions: {
            ...state.configuration.responseOptions,
            default: { country: defaultCountry },
          },
        },
      };
    case extendedFieldActions.SET_INCLUDE_YEAR:
      return {
        ...state,
        configuration: {
          ...state.configuration,
          subfields: {
            ...state.configuration.subfields,
            year: { isVisible: Number(includeYear) },
          },
        },
      };
    case extendedFieldActions.SET_OPTIONS:
      return {
        ...state,
        configuration: {
          ...state.configuration,
          responseOptions: {
            ...state.configuration.responseOptions,
            responseOptionList: options,
          },
        },
      };
    case extendedFieldActions.SET_MULTI_OPTIONS:
      return {
        ...state,
        configuration: {
          ...state.configuration,
          behaviorOptions: { allowMultiple: Boolean(multiOptions) },
        },
      };
    case extendedFieldActions.CLEAR_FIELD_DATA:
      return { ...state, ...defaultFieldData };
    case extendedFieldActions.CLEAR_FIELD_DATA_ON_SAVE:
      return { ...state, ...defaultFieldData, isSavingSignupField: true };
    case extendedFieldActions.SET_IS_NAVIGATING:
      return { ...state, isNavigating };
    case extendedFieldActions.SET_DISPLAY_IS_FULL_WIDTH:
      return { ...state, displayIsFullWidth };
    case extendedFieldActions.SET_ERROR_MESSAGE:
      return { ...state, errorMessage };
    case extendedFieldActions.SET_IS_EVERYONE_GROUP:
      return { ...state, isEveryoneGroup };
    case extendedFieldActions.SET_EDIT_WIDTH_MODAL:
      return { ...state, editWithModal };
    case extendedFieldActions.SET_IS_REFRESHING:
      return { ...state, isRefreshing };
    default:
      return state;
  }
};

const GroupIdSync = ({ groupId }) => {
  const [, setGroupId] = extendedFieldHooks.useGroupId();
  useEffect(() => {
    setGroupId(groupId);
  }, [groupId, setGroupId]);

  return null;
};

const SignupItemIDSync = ({ signupItemId }) => {
  const [, setSignupItemId] = extendedFieldHooks.useSignupItemId();
  useEffect(() => {
    setSignupItemId(signupItemId);
  }, [signupItemId, setSignupItemId]);

  return null;
};

const EditWithModalSync = ({ editWithModal }) => {
  const [, setEditWithModal] = extendedFieldHooks.useEditWithModal();

  useEffect(() => {
    setEditWithModal(editWithModal);
  }, [setEditWithModal, editWithModal]);

  return null;
};

// This is responsible for syncing the active field data when the networkFieldID changes due to user interaction
const SelectedFieldSync = () => {
  const syncSelectedField = extendedFieldHooks.useSyncSelectedField();
  useEffect(syncSelectedField, [syncSelectedField]);

  return null;
};

const NavigationSync = ({ syncNavigation }) => {
  const [isNavigating] = extendedFieldHooks.useIsNavigating();
  useEffect(() => syncNavigation(isNavigating), [isNavigating, syncNavigation]);

  return null;
};

const EditingSync = ({ syncEditing }) => {
  const [isEditing] = extendedFieldHooks.useIsEditing();
  useEffect(() => syncEditing(isEditing), [isEditing, syncEditing]);

  return null;
};

const HasExtendedFieldsSync = ({ syncHasExtendedFields }) => {
  const hasExtendedFields = extendedFieldHooks.useHasExtendedFields();
  useEffect(
    () => syncHasExtendedFields(hasExtendedFields),
    [syncHasExtendedFields, hasExtendedFields]
  );

  return null;
};

const ExtendedFieldsProvider = ({
  children,
  groupId,
  signupItemId,
  editWithModal,
  syncNavigation,
  syncEditing,
  syncHasExtendedFields,
}) => {
  const initialState = {
    groupId,
    signupItemId,
    editWithModal,
    extendedFields: undefined,
    isNavigating: true,
    errorMessage: "",
    displayIsFullWidth: !editWithModal,
    isRefreshing: true, // start off in a loading state
    ...defaultFieldData,
  };

  return (
    <ExtendedFieldsContext.Provider
      value={useReducer(extendedFieldsReducer, initialState)}
    >
      <GroupIdSync groupId={groupId} />
      <SignupItemIDSync signupItemId={signupItemId} />
      <SelectedFieldSync />
      <EditWithModalSync editWithModal={editWithModal} />
      {syncHasExtendedFields && (
        <HasExtendedFieldsSync syncHasExtendedFields={syncHasExtendedFields} />
      )}
      {syncNavigation && <NavigationSync syncNavigation={syncNavigation} />}
      {syncEditing && <EditingSync syncEditing={syncEditing} />}
      {children}
    </ExtendedFieldsContext.Provider>
  );
};

ExtendedFieldsProvider.propTypes = {
  groupId: PropTypes.number,
  signupItemId: PropTypes.number,
};

export { ExtendedFieldsContext, shareWithOptions, defaultFieldData };
export default ExtendedFieldsProvider;
